"use client";

import Image from "next/image";
import { ArrowRight } from "lucide-react";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "../../i18n/navigation";
import styles from "./product-suite.module.css";
import {
  initialProductSuiteState,
  PRODUCT_SUITE_ITEM_COUNT,
  productSuiteReducer,
} from "./product-suite-state";

const productKeys = ["stable", "stopwatch", "planner", "marketplace"] as const;

const productImages = {
  stable: "/landing/stable-2e1a67d79146.webp",
  stopwatch: "/landing/stopwatch-28aee5365c8b.webp",
  planner: "/landing/planner-da9876739e0b.webp",
  marketplace: "/landing/marketplace-1e63e15f8b7f.webp",
} as const satisfies Record<(typeof productKeys)[number], string>;

export function ProductSuite() {
  const locale = useLocale();
  const t = useTranslations("Home.productSuite");
  const [state, dispatch] = useReducer(
    productSuiteReducer,
    initialProductSuiteState,
  );
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [hasLoadedProductImages, setHasLoadedProductImages] = useState(false);
  const [isNavigationHovered, setIsNavigationHovered] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeKey = productKeys[state.activeIndex] ?? productKeys[0];
  const twoDigitNumber = new Intl.NumberFormat(locale, {
    minimumIntegerDigits: 2,
    useGrouping: false,
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);

    return () => {
      mediaQuery.removeEventListener("change", updatePreference);
    };
  }, []);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting =
          entry?.isIntersecting === true && entry.intersectionRatio > 0;
        setIsSectionVisible(isIntersecting);
        if (isIntersecting) {
          setHasLoadedProductImages(true);
        }
      },
      { threshold: 0.01 },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  const isCycleRunning =
    isSectionVisible && !isNavigationHovered && !prefersReducedMotion;

  const selectProduct = (index: number) => {
    setHasLoadedProductImages(true);
    dispatch({ type: "select", index });
  };

  const moveFocus = (index: number) => {
    selectProduct(index);
    buttonRefs.current[index]?.focus();
  };

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let nextIndex: number | undefined;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        nextIndex = (index + 1) % PRODUCT_SUITE_ITEM_COUNT;
        break;
      case "ArrowUp":
      case "ArrowLeft":
        nextIndex =
          (index - 1 + PRODUCT_SUITE_ITEM_COUNT) % PRODUCT_SUITE_ITEM_COUNT;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = PRODUCT_SUITE_ITEM_COUNT - 1;
        break;
    }

    if (nextIndex !== undefined) {
      event.preventDefault();
      moveFocus(nextIndex);
    }
  };

  return (
    <section
      ref={sectionRef}
      id="product-suite"
      aria-labelledby="product-suite-heading"
      className="landing-canvas scroll-mt-[70px] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid items-start gap-12 lg:grid-cols-[minmax(20rem,1fr)_minmax(0,1.5fr)] lg:gap-16">
          <div className="flex flex-col">
            <div>
              <h2
                id="product-suite-heading"
                className="max-w-xl text-balance text-4xl font-bold leading-[1.08] tracking-[-0.035em] text-primary sm:text-5xl sm:leading-[1.08]"
              >
                {t.rich("heading", {
                  accent: (chunks) => (
                    <span className="text-brand-ink">{chunks}</span>
                  ),
                })}
              </h2>
              <p className="mt-5 max-w-lg text-base leading-[1.625] text-secondary">
                {t("description")}
              </p>
            </div>

            <div
              role="tablist"
              aria-label={t("productNavigation")}
              aria-orientation="vertical"
              onMouseEnter={() => setIsNavigationHovered(true)}
              onMouseLeave={() => setIsNavigationHovered(false)}
              className="mt-10 flex flex-col gap-1 border-t border-border-subtle pt-2"
            >
              {productKeys.map((key, index) => {
                const isActive = index === state.activeIndex;

                return (
                  <button
                    key={key}
                    ref={(element) => {
                      buttonRefs.current[index] = element;
                    }}
                    id={`product-suite-tab-${key}`}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls="product-suite-panel"
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => selectProduct(index)}
                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                    className={`group relative flex min-h-16 w-full items-center gap-5 px-3 text-start transition-colors focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus ${
                      isActive ? "bg-brand-subtle/40" : "hover:bg-subtle/60"
                    }`}
                  >
                    <span
                      className={`w-8 shrink-0 font-sans text-sm font-bold uppercase ${
                        isActive ? "text-brand-ink" : "text-secondary"
                      }`}
                    >
                      {twoDigitNumber.format(index + 1)}
                    </span>
                    <span
                      className={`flex-1 text-base ${
                        isActive
                          ? "font-semibold text-primary"
                          : "text-secondary group-hover:text-primary"
                      }`}
                    >
                      {t(`products.${key}.name`)}
                    </span>
                    <span
                      className={`size-2 shrink-0 ${
                        isActive ? "bg-brand" : "bg-border-default"
                      }`}
                      aria-hidden="true"
                    />

                    {isActive && (
                      <span
                        key={`${state.activeIndex}-${state.cycle}`}
                        className={`product-suite-progress ${
                          prefersReducedMotion
                            ? "product-suite-progress--static"
                            : isCycleRunning
                              ? ""
                              : "product-suite-progress--paused"
                        }`}
                        onAnimationEnd={() => {
                          if (isCycleRunning) {
                            dispatch({
                              type: "advance",
                              itemCount: PRODUCT_SUITE_ITEM_COUNT,
                            });
                          }
                        }}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <article
            id="product-suite-panel"
            role="tabpanel"
            aria-labelledby={`product-suite-tab-${activeKey}`}
            className="landing-surface min-w-0 overflow-hidden shadow-elevation-1"
          >
            <div
              className={`product-suite-stage ${
                hasLoadedProductImages ? "" : "product-suite-stage--deferred"
              }`}
              aria-hidden="true"
            >
              {productKeys.map((key, index) => (
                <div
                  key={key}
                  className={`product-suite-stage-card ${
                    index === state.activeIndex ? "is-active" : ""
                  }`}
                >
                  {hasLoadedProductImages && (
                    <>
                      <div className="product-suite-plinth" />
                      <Image
                        key={`${key}-${index === state.activeIndex ? state.cycle : "idle"}`}
                        src={productImages[key]}
                        alt=""
                        fill
                        sizes="(min-width: 1024px) 62vw, 100vw"
                        className={`${styles["productImage"]} object-contain object-center ${
                          key === "stopwatch"
                            ? "p-5 sm:p-7 lg:p-11"
                            : "p-4 sm:p-6 lg:p-10"
                        }`}
                        unoptimized
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-brand-ink">
                {twoDigitNumber.format(state.activeIndex + 1)}
                <span className="mx-3" aria-hidden="true">
                  ·
                </span>
                {t(`products.${activeKey}.name`)}
              </p>
              <h3 className="mt-3 text-3xl font-semibold tracking-[-0.025em] text-primary sm:text-4xl">
                {t(`products.${activeKey}.name`)}
              </h3>
              <p className="mt-3 max-w-3xl text-base leading-[1.625] text-secondary">
                {t(`products.${activeKey}.description`)}
              </p>
              <div className="mt-6">
                <Link
                  href={
                    activeKey === "stable"
                      ? "/product/stable"
                      : `/products/${activeKey}`
                  }
                  className="inline-flex items-center gap-2 text-sm font-bold text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  <span>{t(`products.${activeKey}.name`)}</span>
                  <ArrowRight
                    className="size-4 rtl:-scale-x-100"
                    aria-hidden="true"
                  />
                </Link>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
